import { type WritableDraft } from 'immer';

import { applyPropsPatch, type PartialPropsOf } from '@blaze-runtime/engine-domain';
import {
  getBorderColor,
  type ListDirectionMode,
  type ListLayoutMode,
  type PropertiesConfigInputContainerItemUnifiedBulkActionModel,
  type PropertiesConfigInputContainerItemUnifiedRowActionModel,
  type PropertiesConfigInputListCustomFieldModel,
  type PropertiesConfigInputListFieldModel,
  toFieldSortConfig,
} from '@blaze-shared/elements-domain';

import { type EngineState } from '../../../+state/engine';
import {
  type ContainerFieldMapping,
  getInitialProps,
  patchTitle,
  type ResolvedRecordClick,
  toEngineActionsDisplayMode,
  toEngineBulkActionsDisplayMode,
} from '../../../ai';
import { applyHideWhenEmpty } from '../../../ai/common/hide-when-empty';
import { customFieldLabelPositionForDirection } from '../list.template-appearance';

import { toEngineIndicatorAlign, toEngineSortRequest } from './list.appearance';
import { type ListResolvedContext, type ListTemplate } from './list.definition';
import { isSlotForDirection, type ListFieldSlot, SLOT_TO_FIELD_KEY } from './list.slots';
import {
  type ResolvedListTemplate,
  resolveListTemplate,
  resolveListTemplateImageAppearance,
} from './list.template';

const DEFAULT_DIRECTION: ListDirectionMode = 'vertical';

export interface ResolvedListActions {
  itemActions: PropertiesConfigInputContainerItemUnifiedRowActionModel[];
  bulkActions: PropertiesConfigInputContainerItemUnifiedBulkActionModel[];
  itemClick?: ResolvedRecordClick['props'];
}

interface ListLayoutPatch {
  direction: ListDirectionMode;
  mode?: ListLayoutMode;
  rows?: number;
  cols?: number;
}

export function buildListProps(
  context: ListResolvedContext,
  draft: WritableDraft<EngineState>,
  resolvedActions: ResolvedListActions,
): PartialPropsOf<'list'> {
  let props: PartialPropsOf<'list'> = getInitialProps<'list'>(draft, context.element.id, {
    content: {},
  });

  props = applyBasicContentPatches(props, context);

  const template = context.element.template;
  const resolvedTemplate = template
    ? resolveListTemplate(template, {
        behavior: context.element.behavior,
        mobileBehavior: context.element.mobileBehavior,
        rows: context.element.rows,
        columns: context.element.columns,
      })
    : undefined;
  const direction = resolvedTemplate?.direction ?? DEFAULT_DIRECTION;

  if (template && resolvedTemplate) {
    props = applyTemplatePatch(props, template, resolvedTemplate);
  }

  props = applyRecordActionsPatch(props, context, resolvedActions);
  props = applyRemainingContentPatches(props, context, direction);

  return props;
}

function applyBasicContentPatches(
  props: PartialPropsOf<'list'>,
  context: ListResolvedContext,
): PartialPropsOf<'list'> {
  let patched: PartialPropsOf<'list'> = patchTitle<'list'>(
    props,
    context.element.title,
    context.element.showTitle,
  );

  if (context.element.iconName) {
    patched = applyPropsPatch(patched, {
      content: {
        title: {
          icon: context.element.iconName,
        },
      },
    });
  }

  if (context.element.emptyMessage) {
    patched = applyPropsPatch(patched, {
      content: {
        noDataMessage: context.element.emptyMessage,
      },
    });
  }

  patched = applyHideWhenEmpty(patched, context.element.hideWhenEmpty);

  if (context.element.search !== undefined) {
    patched = applyPropsPatch(patched, {
      content: {
        searchAndFilters: {
          searchEnabled: context.element.search,
        },
      },
    });
  }

  return patched;
}

function applyTemplatePatch(
  props: PartialPropsOf<'list'>,
  template: ListTemplate,
  resolvedTemplate: ResolvedListTemplate,
): PartialPropsOf<'list'> {
  const layout: ListLayoutPatch = { direction: resolvedTemplate.direction };

  if (resolvedTemplate.mode !== undefined) {
    layout.mode = resolvedTemplate.mode;
  }

  if (resolvedTemplate.rows !== undefined) {
    layout.rows = resolvedTemplate.rows;
  }

  if (resolvedTemplate.cols !== undefined) {
    layout.cols = resolvedTemplate.cols;
  }

  let patched: PartialPropsOf<'list'> = applyPropsPatch(props, {
    content: {
      list: { layout },
      ...(resolvedTemplate.mobileScrollMode !== undefined
        ? { mobile: { scrollMode: resolvedTemplate.mobileScrollMode } }
        : {}),
      ...(resolvedTemplate.bulletIcon !== undefined
        ? { subscriptionPlan: { bulletIcon: resolvedTemplate.bulletIcon } }
        : {}),
    },
  });

  const imageAppearance = resolveListTemplateImageAppearance(template, {
    currentImage: patched.style?.image ?? {},
    backgroundRadius: patched.style?.background?.radius ?? '$t.radius',
    elementOutlineColor: getBorderColor(patched.style?.background?.outline, ''),
  });

  patched = applyPropsPatch(patched, {
    style: {
      image: imageAppearance,
    },
  });

  return patched;
}

function applyRecordActionsPatch(
  props: PartialPropsOf<'list'>,
  context: ListResolvedContext,
  resolvedActions: ResolvedListActions,
): PartialPropsOf<'list'> {
  let patched = props;

  if (resolvedActions.itemActions.length > 0) {
    patched = applyPropsPatch(patched, {
      content: {
        unifiedRowActions: resolvedActions.itemActions,
      },
    });
  }

  if (context.element.itemActionsDisplayMode) {
    patched = applyPropsPatch(patched, {
      content: {
        actions: {
          mode: toEngineActionsDisplayMode(context.element.itemActionsDisplayMode),
        },
      },
    });
  }

  if (resolvedActions.bulkActions.length > 0) {
    patched = applyPropsPatch(patched, {
      content: {
        unifiedBulkActions: resolvedActions.bulkActions,
      },
    });
  }

  if (context.element.bulkActionsDisplayMode) {
    patched = applyPropsPatch(patched, {
      content: {
        list: {
          bulkActionsDisplayMode: toEngineBulkActionsDisplayMode(
            context.element.bulkActionsDisplayMode,
          ),
        },
      },
    });
  }

  if (resolvedActions.itemClick) {
    patched = applyPropsPatch(patched, {
      content: {
        rowClick: {
          enabled: resolvedActions.itemClick.enabled,
          action: resolvedActions.itemClick.action,
        },
      },
    });
  }

  return patched;
}

function applyRemainingContentPatches(
  props: PartialPropsOf<'list'>,
  context: ListResolvedContext,
  direction: ListDirectionMode,
): PartialPropsOf<'list'> {
  let patched = props;

  if (context.element.addNewItem) {
    patched = applyPropsPatch(patched, {
      content: {
        itemAdd: {
          enabled: true,
          label: context.element.addNewItem.label || '',
        },
      },
    });
  }

  if (context.element.iconIndicator) {
    const { enabled, iconName, align } = context.element.iconIndicator;

    patched = applyPropsPatch(patched, {
      content: {
        indicator: {
          enabled,
          icon: iconName,
          ...(align ? { align: toEngineIndicatorAlign(align) } : {}),
        },
      },
    });
  }

  if (context.element.sortBy?.length) {
    patched = applyPropsPatch(patched, {
      content: {
        sort: context.element.sortBy.map(toEngineSortRequest).map(toFieldSortConfig),
      },
    });
  }

  if (context.element.fieldMappings?.length) {
    patched = applyPropsPatch(patched, {
      content: resolveFieldMappings(context.element.fieldMappings, direction),
    });
  }

  if (context.customTemplateCode) {
    patched = applyPropsPatch(patched, {
      content: {
        list: {
          customTemplate: {
            code: context.customTemplateCode,
          },
        },
      },
    });
  }

  return patched;
}

function resolveFieldMappings(
  fieldMappings: ContainerFieldMapping<ListFieldSlot>[],
  direction: ListDirectionMode,
): {
  fields: PropertiesConfigInputListFieldModel;
  customFields: PropertiesConfigInputListCustomFieldModel[];
} {
  const fields: PropertiesConfigInputListFieldModel = {};
  const customFields: PropertiesConfigInputListCustomFieldModel[] = [];
  const labelBehavior = customFieldLabelPositionForDirection(direction);

  fieldMappings.forEach(({ label, field, mapping }) => {
    if (mapping && isSlotForDirection(direction, mapping)) {
      fields[SLOT_TO_FIELD_KEY[mapping]] = field;
      return;
    }

    customFields.push({ label, column: field, labelBehavior });
  });

  return { fields, customFields };
}
